import { Type } from "class-transformer";

import { Company } from "../../models/company.model";
import { SlotsBatch } from "../../models/slots-batch.model";
import { endOfDay } from "../../utilities/date";
import { Negotiation } from "../modules/commercial/models/negotiation.model";
import { Contract } from "../modules/contracts/models/contract.model";

export enum SlotRequestStatus {
  UNKNOWN = 0,
  PENDING = 1,
  EXPIRED = 2,
  ASSIGNED = 3,
  ASSIGNED_PARTIALLY = 4,
  RETIRED = 5,
  REJECTED = 6
};

const StatusLocaleNames = Object.freeze({
  [SlotRequestStatus.UNKNOWN]: 'ERROR',
  [SlotRequestStatus.PENDING]: 'SLOTS.STATUS.PENDING',
  [SlotRequestStatus.EXPIRED]: 'SLOTS.STATUS.EXPIRED',
  [SlotRequestStatus.ASSIGNED]: 'SLOTS.STATUS.ASSIGNED',
  [SlotRequestStatus.ASSIGNED_PARTIALLY]: 'SLOTS.STATUS.ASSIGNED_PARTIALLY',
  [SlotRequestStatus.RETIRED]: 'SLOTS.STATUS.WITHDRAWN',
  [SlotRequestStatus.REJECTED]: 'SLOTS.STATUS.REJECTED',
});

export class RequestStatus {
  public id: SlotRequestStatus;
  public name: string;
  public description: string;

  public get locale_name(): string {
    return StatusLocaleNames[this.id] ?? StatusLocaleNames[SlotRequestStatus.UNKNOWN];
  }

  constructor(data: Partial<RequestStatus> = {}) {
    Object.assign(this, data);
  }
}

export class SlotsRequest {
  constructor(data: Partial<SlotsRequest> = {}) {
    Object.assign(this, data);
  }

  readonly id?: number;
  quantity: number;
  /** Cupos asignados (acumulado por batches aceptados). */
  assigned: number;
  /** Cupos rechazados explícitamente al usar "Rechazar restantes" desde estado parcial. */
  rejected: number;

  /**
   * If there is a related {@link Negotiation} to this Batch.
   */
  @Type(() => Negotiation)
  negotiation: Negotiation;

  @Type(() => Contract)
  contract: Contract;

  @Type(() => Date)
  date: Date;

  @Type(() => Date)
  readonly created_at?: Date;

  /**
   * {@link Company} that request the slots.
   */
  @Type(() => Company)
  readonly company?: Company;

  /**
   * {@link Company} that will receive the slots.
   */
  @Type(() => Company)
  recipient: Company;

  /**
   * Additional instructions or comments defined by the
   * [[SlotsBatch.allocator|Allocator]].
   */
  observations?: string;

  /**
   * Slots assigned for this Request
   */
  @Type(() => SlotsBatch)
  readonly batches?: SlotsBatch[];

  @Type(() => RequestStatus)
  public status: RequestStatus;

  /**
   * Quantity of slots assigned for this {@link SlotsRequest} .
   * @returns The total number of slots assigned in the {@link SlotsRequest}.
   */
  public get assignedQuantity(): number {
    const batchIds = new Set(this.batches.map(b => b.id));
    const batchesFiltered = this.batches.filter(batch =>
      (batch.status.id === 1 || batch.status.id === 2) &&
      !batchIds.has(batch.parent_id) // Solo batches raíz para evitar doble conteo
    );

    let total = 0;
    batchesFiltered.forEach(batch => {
      total += batch.slots_total
    });

    return total;
  }

  /**
   * Percentage of slots assigned for this Request.
   */
  public get assignedPercentage(): number {
    if (this.quantity === 0 || this.assignedQuantity === 0) return 0;
    const percentage = (this.assignedQuantity / this.quantity);
    return percentage;
  }

  /**
   * Percentage of slots rejected for this Request.
   * Returns 1 when fully rejected, or the partial rejection ratio.
   */
  public get rejectedPercentage(): number {
    if (this.status?.id === SlotRequestStatus.REJECTED) return 1;
    if (this.quantity === 0 || !this.rejected) return 0;
    return this.rejected / this.quantity;
  }

  /** True cuando la solicitud fue parcialmente asignada y los cupos restantes fueron rechazados. */
  public get hasRejectedRemaining(): boolean {
    return this.status?.id === SlotRequestStatus.ASSIGNED_PARTIALLY && !!this.rejected;
  }

  /** True cuando la solicitud pertenece a "Cupos Devueltos/Rechazados": rechazada totalmente o parcial con devueltos. */
  public get isRejectedOrReturned(): boolean {
    return this.status?.id === SlotRequestStatus.REJECTED || this.hasRejectedRemaining;
  }

  /** Cantidad de cupos devueltos (batches con status 5 - RETURNED). */
  public get returnedQuantity(): number {
    return this.batches
      ?.filter(b => b.status?.id === 5)
      .reduce((sum, b) => sum + (b.slots_total || 0), 0) ?? 0;
  }

  public get expired(): boolean {
    if (this.status?.id !== SlotRequestStatus.EXPIRED) {
      const dueDate = endOfDay(this.date);
      dueDate.setDate(dueDate.getDate() + 1); // Expira el dia posterior al solicitado

      const today = new Date();

      if (dueDate.getTime() < today.getTime()) {
        return true;
        // this.status = new RequestStatus({
        //   id: SlotRequestStatus.EXPIRED,
        //   name: 'expired'
        // });
      }
    }

    return this.status?.id === SlotRequestStatus.EXPIRED;
  }
}
